import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '@/lib/api-client';

interface PushSubscriptionData {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

/**
 * Hook for managing push notifications.
 * 
 * IMPORTANT: Push subscriptions are per-device (browser), but we associate them with users.
 * When a user logs in on a device that already has a push subscription from a different user,
 * we need to re-register that subscription for the new user.
 * 
 * @param userId - The current logged-in user's ID. Pass undefined/null if not logged in.
 */
export const usePushNotifications = (userId?: string | null) => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSubscribedForCurrentUser, setIsSubscribedForCurrentUser] = useState(false);
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  
  // Track if we've already verified/re-registered for this user to avoid duplicate API calls
  const lastVerifiedUserId = useRef<string | null>(null);

  // Initialize push support and check local subscription
  useEffect(() => {
    const init = async () => {
      // Detect iOS Safari (Web Push only works on iOS 16.4+ when installed to home screen)
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                          (navigator as Navigator & { standalone?: boolean }).standalone === true;
      
      // Check if push notifications are supported
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.debug('[Push] Not supported:', {
          serviceWorker: 'serviceWorker' in navigator,
          PushManager: 'PushManager' in window,
          isIOS,
          isStandalone,
          hint: isIOS && !isStandalone ? 'iOS requires app to be added to home screen for push notifications' : undefined
        });
        setLoading(false);
        return;
      }

      setIsSupported(true);
      const currentPermission = Notification.permission;
      setPermission(currentPermission);
      console.debug('[Push] Initialized:', { permission: currentPermission, isIOS, isStandalone });

      try {
        // Wait for service worker to be ready (with timeout)
        const swReady = navigator.serviceWorker.ready;
        const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000));
        const registration = await Promise.race([swReady, timeout]);

        if (registration) {
          const sub = await registration.pushManager.getSubscription();
          console.debug('[Push] Local browser subscription exists:', !!sub);
          setSubscription(sub);
          // Note: isSubscribed means browser has a subscription, but it might belong to a different user
          setIsSubscribed(!!sub);
        } else {
          console.debug('[Push] Service worker registration timed out');
        }
      } catch (error) {
        console.error('Error checking push subscription:', error);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  // When user changes, verify the subscription belongs to them and re-register if needed
  // Also handles auto-recovery when permission is granted but subscription expired
  useEffect(() => {
    const verifyAndReRegister = async () => {
      // Skip if no user, push not supported, or still loading
      if (!userId || !isSupported || loading) {
        return;
      }

      // Skip if we already verified for this user
      if (lastVerifiedUserId.current === userId) {
        return;
      }

      setVerifying(true);

      try {
        // Case 1: No browser subscription exists but permission is granted
        // This means the subscription expired/was revoked - auto-recover by re-subscribing
        if (!subscription && permission === 'granted') {
          console.debug('[Push] Permission granted but no subscription - auto-recovering');
          
          const registration = await navigator.serviceWorker.ready;
          const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
          
          if (vapidPublicKey) {
            try {
              const newSubscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
              });
              
              setSubscription(newSubscription);
              setIsSubscribed(true);
              
              const subscriptionData: PushSubscriptionData = {
                endpoint: newSubscription.endpoint,
                keys: {
                  p256dh: arrayBufferToBase64(newSubscription.getKey('p256dh')!),
                  auth: arrayBufferToBase64(newSubscription.getKey('auth')!)
                }
              };

              await apiClient.post('/notifications/subscribe', {
                ...subscriptionData,
                userAgent: navigator.userAgent,
              });
              
              console.debug('[Push] Auto-recovered subscription for user');
              setIsSubscribedForCurrentUser(true);
              lastVerifiedUserId.current = userId;
              return;
            } catch (subscribeError) {
              console.error('[Push] Auto-recovery failed:', subscribeError);
            }
          }
          
          setIsSubscribedForCurrentUser(false);
          return;
        }

        // Case 2: No subscription and permission not granted - user needs to subscribe fresh
        if (!subscription) {
          setIsSubscribedForCurrentUser(false);
          return;
        }

        // Case 3: Subscription exists - verify it belongs to current user
        console.debug('[Push] Verifying subscription belongs to current user:', { userId, endpoint: subscription.endpoint.substring(0, 50) + '...' });

        // Check if this browser's subscription endpoint is registered for the current user
        const response = await apiClient.post<{ success: boolean; isRegisteredForCurrentUser: boolean }>(
          '/notifications/check-endpoint',
          { endpoint: subscription.endpoint }
        );

        if (response.isRegisteredForCurrentUser) {
          console.debug('[Push] Subscription is registered for current user');
          setIsSubscribedForCurrentUser(true);
          lastVerifiedUserId.current = userId;
        } else {
          // Subscription exists but belongs to a different user - re-register it for current user
          console.debug('[Push] Subscription belongs to different user, re-registering for current user');
          
          const subscriptionData: PushSubscriptionData = {
            endpoint: subscription.endpoint,
            keys: {
              p256dh: arrayBufferToBase64(subscription.getKey('p256dh')!),
              auth: arrayBufferToBase64(subscription.getKey('auth')!)
            }
          };

          await apiClient.post('/notifications/subscribe', {
            ...subscriptionData,
            userAgent: navigator.userAgent,
          });

          console.debug('[Push] Re-registered subscription for current user');
          setIsSubscribedForCurrentUser(true);
          lastVerifiedUserId.current = userId;
        }
      } catch (error) {
        console.error('[Push] Error verifying/re-registering subscription:', error);
        setIsSubscribedForCurrentUser(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyAndReRegister();
  }, [userId, isSupported, subscription, permission, loading]);

  // Reset verification when user logs out
  useEffect(() => {
    if (!userId) {
      lastVerifiedUserId.current = null;
      setIsSubscribedForCurrentUser(false);
    }
  }, [userId]);

  // Re-check subscription when app returns to foreground (visibility change)
  // This handles Android Chrome killing subscriptions when user swipes away the app
  useEffect(() => {
    if (!isSupported || !userId) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      
      console.debug('[Push] App returned to foreground, checking subscription');
      
      try {
        const registration = await navigator.serviceWorker.ready;
        const currentSub = await registration.pushManager.getSubscription();
        
        // If we had a subscription but now it's gone, trigger re-verification
        if (subscription && !currentSub) {
          console.debug('[Push] Subscription was invalidated while app was backgrounded');
          setSubscription(null);
          setIsSubscribed(false);
          // Reset verification to trigger auto-recovery
          lastVerifiedUserId.current = null;
        } else if (!subscription && currentSub) {
          // Browser created a new subscription (rare but possible)
          console.debug('[Push] New subscription detected');
          setSubscription(currentSub);
          setIsSubscribed(true);
          lastVerifiedUserId.current = null;
        }
      } catch (error) {
        console.error('[Push] Error checking subscription on visibility change:', error);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isSupported, userId, subscription]);

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const sub = await registration.pushManager.getSubscription();
      setSubscription(sub);
      setIsSubscribed(!!sub);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const requestPermission = async (): Promise<boolean> => {
    if (!('Notification' in window)) {
      console.error('This browser does not support notifications');
      return false;
    }

    const permission = await Notification.requestPermission();
    setPermission(permission);
    return permission === 'granted';
  };

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      console.error('Push notifications are not supported');
      return false;
    }

    try {
      // Request permission first
      const hasPermission = await requestPermission();
      if (!hasPermission) {
        console.error('Notification permission denied');
        return false;
      }

      // Register service worker if not already registered
      let registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        registration = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;
      }

      // Get VAPID public key from environment or config
      const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
      if (!vapidPublicKey) {
        console.error('VAPID public key not configured');
        return false;
      }

      // Subscribe to push notifications
      const newSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      setSubscription(newSubscription);
      setIsSubscribed(true);

      // Send subscription to backend
      const subscriptionData: PushSubscriptionData = {
        endpoint: newSubscription.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(newSubscription.getKey('p256dh')!),
          auth: arrayBufferToBase64(newSubscription.getKey('auth')!)
        }
      };

      await apiClient.post('/notifications/subscribe', {
        ...subscriptionData,
        userAgent: navigator.userAgent,
      });
      
      // Mark as subscribed for current user
      setIsSubscribedForCurrentUser(true);
      if (userId) {
        lastVerifiedUserId.current = userId;
      }
      
      return true;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      return false;
    }
  }, [isSupported, userId]);

  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!subscription) {
      return false;
    }

    try {
      await subscription.unsubscribe();
      setSubscription(null);
      setIsSubscribed(false);
      setIsSubscribedForCurrentUser(false);
      lastVerifiedUserId.current = null;

      // Notify backend
      await apiClient.post('/notifications/unsubscribe', {
        endpoint: subscription.endpoint
      });

      return true;
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      return false;
    }
  }, [subscription]);

  return {
    isSupported,
    isSubscribed, // Browser has a subscription (may belong to different user)
    isSubscribedForCurrentUser, // Subscription is verified to belong to current user
    permission,
    loading: loading || verifying, // Include verification in loading state
    subscribe,
    unsubscribe,
    requestPermission
  };
};

// Helper functions
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}
